import React, {useState, useRef, useEffect, useCallback} from 'react';
import {useViewport} from './useViewport.js';
import {useWindowPlacer} from './useWindowPlacer.js';
import {useWindowResizer} from './useWindowResizer.js';
import {
    setPresentationWindowPosition,
    setBulkPresentationWindowPositions,
    setEdgesPadding,
    setEdgesSize,
    setEditorPadding,
    setZObjectSize,
    setSelectedPulledElements,
    setBulkWindowPositions,
    setWindowPosition} from 'actions/index.js';
import {NW, NE, SW, SE} from 'components/windows/resize-types.js';

export const useWindowManager = ({
    isSimple=false,
    sizeRef,
    sequencerEngine,
    initialCoords,
    expand,
    ref,
    containerRef,
    size,
    basicContainerRef,
    setSize,
    element,
    elements,
    isPad,
    ordering,
    setOrdering,
    windowPosition,
    skipPlacement
}) => {

    let paddingOnDown = useRef(undefined);
    let [isDragging, setIsDragging] = useState(false);
    // let [coords, setCoords] = useState(initialCoords);
    let startingPositions = useRef(undefined);
    let startingPoint = useRef(undefined);
    let [offset, setOffset] = useState({x: 0, y: 0});
    let [isResizing, setIsResizing] = useState(undefined);
    let [windowScale, setWindowScale] = useState({x: 0, y:0});
    let [_coords, _setCoords] = useState(initialCoords);

    const setCoords = useCallback((coords) => {
        _setCoords(coords);
        if (sequencerEngine && coords && element) {
            if (sequencerEngine.state.spatial.presentationMode) {
                sequencerEngine.dispatch(
                    setPresentationWindowPosition(element.id, coords));
            } else {
                sequencerEngine.dispatch(
                    setWindowPosition(element.id, coords));
            }
        }
    }, [sequencerEngine, element, _setCoords]);

    const {handleScroll} = useViewport({sequencerEngine});
    const {adjust, placeWindow} = useWindowPlacer({
        element,
        skipPlacement,
        setWindowScale,
        sequencerEngine,
        ref,
        setSize,
        size,
        sizeRef,
        containerRef,
        setCoords,
        handleScroll,
        elements
    });

    const updateOrdering = useCallback(() => {
        if (!ordering) {
            return;
        }
        if (ordering.length > 0 && ordering.indexOf(element) === ordering.length - 1) {
            return;
        }
        let _ordering = ordering.filter(x => x !== element);
        _ordering = [... _ordering, element];
        setOrdering(_ordering);
    }, [ordering, setOrdering, element]);
 
    const getXY = useCallback((e,skipScale) => {
        let x = e.clientX;
        let y = e.clientY;
        if (containerRef) {
            let rect = containerRef.current.getBoundingClientRect();
            x = e.clientX - rect.left - offset.x;
            y = e.clientY - rect.top - offset.y;
            let scale = window._scale || 1;
            if (!skipScale) {
                x/=scale;
                y/=scale;
            }
        }
        return {x,y};
    }, [containerRef, offset]);

    const {startResizing, resizeWindow} = useWindowResizer({
        ref,
        setCoords,
        sequencerEngine,
        setWindowScale,
        windowScale,
        adjust,
        updateOrdering,
        setIsResizing,
        getXY
    });

    useEffect(() => {
        if (windowPosition) {
            adjust(windowPosition.x, windowPosition.y);
            element.x = windowPosition.x;
            element.y = windowPosition.y;
        }
    }, [windowPosition, setSize]);

    useEffect(() => {
        updateOrdering();
    }, []);

    const isInitialScale = (scale) => scale.x === 0 && scale.y === 0;

    const getSelected = () => {
        if (sequencerEngine) {
            let x = sequencerEngine.state.windows.selectedElements;
            return Object.keys(x);
        }
        return [];
    };

    useEffect(() => {
        if (element && element.x !== undefined) {
            setTimeout(() => {
                adjust(element.x, element.y);
            }, 50);
        }
    }, [expand]);

    useEffect(() => {
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mousedown", onWindowDown);
        window.addEventListener("mouseup", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mousedown", onWindowDown);
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [
        setOffset,
        setCoords,
        element,
        isDragging,
        offset,
        setSize,
        isResizing,
        isDragging,
        setIsResizing,
        windowScale,
        setWindowScale]);

    const onWindowDown = useCallback((e) => {
    }, []);

    const onMouseDown = useCallback((e) => {
        e.stopPropagation();
        let rect = ref.current.getBoundingClientRect();


        startingPoint.current = {x: e.pageX, y: e.pageY};

        if (sequencerEngine) {
            paddingOnDown.current = {... sequencerEngine.state.spatial.editorPadding};
            if (sequencerEngine.state.zobject.uxLocked ||
                sequencerEngine.state.spatial.lockedObjects[element.id]) {
                return;
            }
            let pts = {};
            let presenting = sequencerEngine.state.spatial.presentationMode;
            let windowPositions = sequencerEngine.state.windows.windowPositions;
            let presentationPositions = sequencerEngine.state.windows.presentationWindowPositions;
            for (let id in elements.current) {
                let pos = presenting ? presentationPositions[id] || windowPositions[id] : windowPositions[id];
                pts[id] = {... pos};
            }
            startingPositions.current = pts;
        }
 
        if (sequencerEngine) {
            if (!getSelected().includes(element.id)) {
                sequencerEngine.dispatch(
                    setSelectedPulledElements({}));
            }
        }
        setOffset({x: e.clientX - rect.left, y: e.clientY - rect.top});
        setIsDragging(true);
        updateOrdering();
    }, [setOffset, setIsDragging, ordering, setOrdering]);

    const onContextMenu = useCallback((e) => {
        e.stopPropagation();
        updateOrdering();
    }, [ordering, setOrdering]);

    const onMouseUp = useCallback(() => {
        if (isDragging || isResizing) {
            setIsResizing(false);
            setIsDragging(false);
            setOffset({x: 0, y: 0});
        }
    }, [setIsDragging, setIsResizing, setOffset, isResizing, isDragging]);

    const onMouseMove = useCallback((e) => {

        if (!isDragging && !isResizing) { return; }
        let {x,y} = getXY(e);

        if (sequencerEngine && paddingOnDown.current) {
            let editorPadding = sequencerEngine.state.spatial.editorPadding;
            let left = editorPadding.x - paddingOnDown.current.x;
            let top = editorPadding.y - paddingOnDown.current.y;
            x += left;
            y += top;
        }

        if (!containerRef && !isResizing) {
            let _coords = {x: e.pageX - offset.x, y: e.pageY - offset.y};
            if (basicContainerRef) {
                let rect = basicContainerRef.current.getBoundingClientRect();
                _coords.x -= rect.left;
                _coords.y -= rect.top;
            }

            if (element && element.subSequence && element.subSequence.getRootSubSequence) {
                //_coords.x = Math.max(0, _coords.x);
                _coords.y = Math.max(0, _coords.y);
            }

            setCoords(_coords);
            return;
        }

        if (isDragging) {
            let selected = getSelected();
            let xy = {x: e.pageX, y: e.pageY};
            x += containerRef.current.scrollLeft;
            y += containerRef.current.scrollTop;
            if (selected.length > 0 && xy && startingPoint.current) {
                let xDiff = xy.x - startingPoint.current.x;
                let yDiff = xy.y - startingPoint.current.y;
                let positions = {};
                for (let id of selected) {
                    if (id === element.id) {
                        continue;
                    }
                    // otherwise
                    let position = startingPositions.current[id];
                    let scale = window._scale || 1;
                    if (position) {
                        positions[id] = {
                            x: position.x + (xDiff)/scale,
                            y: position.y + (yDiff)/scale,
                        };
                    }
                }
                if (sequencerEngine.state.spatial.presentationMode) {
                    sequencerEngine.dispatch(setBulkPresentationWindowPositions(positions));
                } else {
                    sequencerEngine.dispatch(setBulkWindowPositions(positions));
                }
            }

            if (element && element.subSequence && element.subSequence.getRootSubSequence) {
                x = Math.max(0, x);
                y = Math.max(0, y);
            }

            adjust(x,y);
            setCoords({x,y});

            handleScroll(x,y, sizeRef);

            element.x = x;
            element.y = y;
        } else if (isResizing) {
            resizeWindow(isResizing, x, y, element);
        }
 
    }, [isDragging, element, setOffset, setCoords, offset, setSize, elements, setWindowScale, windowScale, isResizing]);

   
    let style = {};
    if (ordering && ordering.includes(element)) {
        let index = ordering.indexOf(element);
        style.zIndex = 10 * (1 + index);
    }

    if (isSimple) {
        return {
            //setWindowScale,
            isDragging,
            zIndex: style.zIndex,
            onContextMenu,
            onMouseDown,
            onMouseUp,
            startResizing
        };
    }

    if (_coords) {
        style.top = _coords.y;
        style.left = _coords.x;
    }
    return {
        adjust,
        onContextMenu,
        setWindowScale,
        style,
        isDragging,
        onMouseDown,
        windowScale,
        onMouseUp,
        setCoords,
        isDragging,
        startResizing,
        coords: _coords,
        zIndex: style.zIndex
    };
};

